import { z } from "zod";
import { ServiceTemplateRecurrencePeriod } from "@common/types/serviceTemplateRecurrencePeriod";
import { ServiceTemplateSchedulesSortValue } from "@common/types/views/serviceTemplateSchedulesView";
import { ENGAGEMENT_TEAM_ROLES } from "@common/types/taskTemplateAssigneeRole";
import {
    MonthRecurrenceType,
    QuarterMonth,
    RecurrenceLeadUnit,
    RelativeCalendarPeriod,
    RelativeCalendarPosition,
    RelativeDayType,
    ServiceTemplateRecurrenceStatus,
    WorkEndDateMode,
    WorkStartDateMode,
} from "@common/types/serviceTemplateRecurrence";
import {
    refineEndsExclusivity,
    refineServiceStartPositioning,
} from "@common/schemas/serviceTemplateRecurrence.refinements";

// ─── Path params ───────────────────────────────────────────────────────────────

export const ServiceTemplateSchedulePathParamsSchema = z.object({
    organizationId: z.string().uuid(),
});

export const ServiceTemplateScheduleIdPathParamsSchema = z.object({
    scheduleId: z.string().uuid(),
});

// ─── List query ────────────────────────────────────────────────────────────────

// Query arrays reach the API via `qs`, which yields a scalar for a single
// occurrence (`?periods=daily`) and an array for repeated keys. Wrap a scalar
// so both shapes validate, mirroring `entityIdsQuerySchema`. Kept `.optional()`
// by the caller, so an omitted key stays `undefined` rather than `[]`.
function arrayQueryParam<TElement extends z.ZodTypeAny>(element: TElement) {
    return z.preprocess(
        (value) => (Array.isArray(value) ? value : [value]),
        z.array(element),
    );
}

export const ServiceTemplateScheduleListQuerySchema = z.object({
    templateId: z.string().uuid().optional(),
    // Schedules page Filters overlay: Service (close template) multi-select,
    // distinct from the single-template `templateId` scope above (used by
    // the Templates page's "Go to schedules" action).
    templateIds: arrayQueryParam(z.string().uuid()).optional(),
    entityUuid: z.string().uuid().optional(),
    // Free-text search matched against the template name and entity name
    // (see ServiceTemplateScheduleService.listSchedules for the query).
    search: z.string().trim().min(1).optional(),
    // Optional pagination. When omitted the full result set is returned to
    // preserve backwards compatibility; callers can page large orgs with these.
    limit: z.coerce.number().int().positive().max(200).optional(),
    offset: z.coerce.number().int().nonnegative().optional(),
    // Optional single-column sort. Omitted / cleared falls back to the page
    // default (START_DATE_DESC) resolved in the query service.
    sort: z.nativeEnum(ServiceTemplateSchedulesSortValue).optional(),
    // Schedules page Filters overlay: recurrence period multi-select.
    periods: arrayQueryParam(
        z.nativeEnum(ServiceTemplateRecurrencePeriod),
    ).optional(),
    // Schedules page Filters overlay: engagement team, matched against the
    // schedule's preparer/reviewer/manager. Operators mirror the Work page's
    // engagement team filter ("any"/"every"); default "any" when omitted.
    engagementTeamRoles: arrayQueryParam(
        z.enum(ENGAGEMENT_TEAM_ROLES),
    ).optional(),
    engagementTeamRoleOperator: z.enum(["any", "every"]).optional(),
    engagementTeamMemberIds: arrayQueryParam(
        z.coerce.number().int(),
    ).optional(),
    engagementTeamMemberOperator: z.enum(["any", "every"]).optional(),
    // Schedules page Filters overlay: Client (workspace) multi-select,
    // matched against the schedule's workspaceId.
    clientIds: arrayQueryParam(z.string().uuid()).optional(),
});

export type ServiceTemplateScheduleListQuery = z.infer<
    typeof ServiceTemplateScheduleListQuerySchema
>;

// ─── Bulk action ────────────────────────────────────────────────────────────

export const ServiceTemplateScheduleBulkActionTypeSchema = z.enum([
    "pause",
    "resume",
    "delete",
]);

export type ServiceTemplateScheduleBulkActionType = z.infer<
    typeof ServiceTemplateScheduleBulkActionTypeSchema
>;

export const ServiceTemplateScheduleBulkActionBodySchema = z.object({
    scheduleIds: z.array(z.string().uuid()).min(1).max(500),
    action: ServiceTemplateScheduleBulkActionTypeSchema,
});

export type ServiceTemplateScheduleBulkActionBody = z.infer<
    typeof ServiceTemplateScheduleBulkActionBodySchema
>;

export const ServiceTemplateScheduleBulkActionResponseSchema = z.object({
    succeededIds: z.array(z.string().uuid()),
    failed: z.array(
        z.object({
            scheduleId: z.string().uuid(),
            message: z.string(),
        }),
    ),
});

export type ServiceTemplateScheduleBulkActionResponse = z.infer<
    typeof ServiceTemplateScheduleBulkActionResponseSchema
>;

// ─── Shared refinement helpers ─────────────────────────────────────────────────

function addRequiredIssue(
    ctx: z.RefinementCtx,
    path: string,
    message: string,
): void {
    ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [path],
        message,
    });
}

function refineWorkStartMode(
    value: {
        workStartDateMode?: WorkStartDateMode;
        workStartOffsetDays?: number | null;
        workStartSkipWeekend?: boolean;
        workStartWeekday?: number | null;
        workStartRelativePeriod?: RelativeCalendarPeriod | null;
        workStartRelativePosition?: RelativeCalendarPosition | null;
        workStartRelativeDayType?: RelativeDayType | null;
        workStartRelativeDayOfPeriod?: number | null;
    },
    ctx: z.RefinementCtx,
): void {
    if (
        value.workStartDateMode ===
            WorkStartDateMode.OFFSET_DAYS_AFTER_SERVICE_END &&
        (value.workStartOffsetDays === null ||
            value.workStartOffsetDays === undefined)
    ) {
        addRequiredIssue(
            ctx,
            "workStartOffsetDays",
            "workStartOffsetDays is required for OFFSET_DAYS_AFTER_SERVICE_END",
        );
    }
    if (
        value.workStartDateMode !== undefined &&
        value.workStartDateMode !==
            WorkStartDateMode.OFFSET_DAYS_AFTER_SERVICE_END &&
        value.workStartSkipWeekend
    ) {
        addRequiredIssue(
            ctx,
            "workStartSkipWeekend",
            "workStartSkipWeekend only applies to OFFSET_DAYS_AFTER_SERVICE_END",
        );
    }
    if (
        value.workStartDateMode ===
            WorkStartDateMode.NEXT_WEEKDAY_AFTER_SERVICE_END &&
        (value.workStartWeekday === null ||
            value.workStartWeekday === undefined)
    ) {
        addRequiredIssue(
            ctx,
            "workStartWeekday",
            "workStartWeekday is required for NEXT_WEEKDAY_AFTER_SERVICE_END",
        );
    }
    if (
        value.workStartDateMode ===
        WorkStartDateMode.RELATIVE_CALENDAR_DATE_AFTER_SERVICE_END
    ) {
        if (
            value.workStartRelativePeriod === null ||
            value.workStartRelativePeriod === undefined
        ) {
            addRequiredIssue(
                ctx,
                "workStartRelativePeriod",
                "workStartRelativePeriod is required for RELATIVE_CALENDAR_DATE",
            );
        }
        if (
            value.workStartRelativePosition === null ||
            value.workStartRelativePosition === undefined
        ) {
            addRequiredIssue(
                ctx,
                "workStartRelativePosition",
                "workStartRelativePosition is required for RELATIVE_CALENDAR_DATE",
            );
        }
        if (
            value.workStartRelativeDayType === null ||
            value.workStartRelativeDayType === undefined
        ) {
            addRequiredIssue(
                ctx,
                "workStartRelativeDayType",
                "workStartRelativeDayType is required for RELATIVE_CALENDAR_DATE",
            );
        }
        if (
            value.workStartRelativeDayType === RelativeDayType.DAY &&
            (value.workStartRelativeDayOfPeriod === null ||
                value.workStartRelativeDayOfPeriod === undefined)
        ) {
            addRequiredIssue(
                ctx,
                "workStartRelativeDayOfPeriod",
                "workStartRelativeDayOfPeriod is required when workStartRelativeDayType is DAY",
            );
        }
    }
}

function refineWorkEndMode(
    value: {
        workEndDateMode?: WorkEndDateMode;
        workEndOffsetDays?: number | null;
        workEndRelativePeriod?: RelativeCalendarPeriod | null;
        workEndRelativePosition?: RelativeCalendarPosition | null;
        workEndRelativeDayType?: RelativeDayType | null;
        workEndRelativeDayOfPeriod?: number | null;
    },
    ctx: z.RefinementCtx,
): void {
    if (
        value.workEndDateMode ===
            WorkEndDateMode.OFFSET_DAYS_AFTER_WORK_START &&
        (value.workEndOffsetDays === null ||
            value.workEndOffsetDays === undefined)
    ) {
        addRequiredIssue(
            ctx,
            "workEndOffsetDays",
            "workEndOffsetDays is required for OFFSET_DAYS_AFTER_WORK_START",
        );
    }
    if (
        value.workEndDateMode ===
        WorkEndDateMode.RELATIVE_CALENDAR_DATE_AFTER_WORK_START
    ) {
        if (
            value.workEndRelativePeriod === null ||
            value.workEndRelativePeriod === undefined
        ) {
            addRequiredIssue(
                ctx,
                "workEndRelativePeriod",
                "workEndRelativePeriod is required for RELATIVE_CALENDAR_DATE",
            );
        }
        if (
            value.workEndRelativePosition === null ||
            value.workEndRelativePosition === undefined
        ) {
            addRequiredIssue(
                ctx,
                "workEndRelativePosition",
                "workEndRelativePosition is required for RELATIVE_CALENDAR_DATE",
            );
        }
        if (
            value.workEndRelativeDayType === null ||
            value.workEndRelativeDayType === undefined
        ) {
            addRequiredIssue(
                ctx,
                "workEndRelativeDayType",
                "workEndRelativeDayType is required for RELATIVE_CALENDAR_DATE",
            );
        }
        if (
            value.workEndRelativeDayType === RelativeDayType.DAY &&
            (value.workEndRelativeDayOfPeriod === null ||
                value.workEndRelativeDayOfPeriod === undefined)
        ) {
            addRequiredIssue(
                ctx,
                "workEndRelativeDayOfPeriod",
                "workEndRelativeDayOfPeriod is required when workEndRelativeDayType is DAY",
            );
        }
    }
}

function refineApplicationLead(
    value: {
        applicationLeadValue?: number;
        applicationLeadUnit?: RecurrenceLeadUnit;
    },
    ctx: z.RefinementCtx,
): void {
    if (
        value.applicationLeadValue != null &&
        value.applicationLeadValue > 0 &&
        value.applicationLeadUnit == null
    ) {
        addRequiredIssue(
            ctx,
            "applicationLeadUnit",
            "applicationLeadUnit is required when applicationLeadValue is greater than 0",
        );
    }
}

// ─── Shared refinement ─────────────────────────────────────────────────────────

// Applies the mode-dependent required-field rules shared by the create and
// update payloads. Positioning fields are coalesced to `null` because the
// shared `refineServiceStartPositioning` helper distinguishes "not applicable"
// (null) from "omitted from a partial update" (undefined).
export function refineSchedulePositioning(
    value: Parameters<typeof refineWorkStartMode>[0] &
        Parameters<typeof refineWorkEndMode>[0] &
        Parameters<typeof refineApplicationLead>[0] & {
            period?: ServiceTemplateRecurrencePeriod;
            weekDay?: number | null;
            weekDays?: number[] | null;
            month?: number | null;
            dateOfMonth?: number | null;
            datesOfMonth?: number[] | null;
            monthRecurrenceType?: MonthRecurrenceType | null;
            quarterMonth?: QuarterMonth | null;
            endDate?: string | null;
            maxOccurrenceCount?: number | null;
        },
    ctx: z.RefinementCtx,
): void {
    refineServiceStartPositioning(
        {
            ...value,
            weekDay: value.weekDay ?? null,
            weekDays: value.weekDays ?? null,
            dateOfMonth: value.dateOfMonth ?? null,
            datesOfMonth: value.datesOfMonth ?? null,
            monthRecurrenceType: value.monthRecurrenceType ?? null,
        },
        ctx,
    );
    refineEndsExclusivity(value, ctx);
    refineWorkStartMode(value, ctx);
    refineWorkEndMode(value, ctx);
    refineApplicationLead(value, ctx);
}

// Rejects an end date that precedes the start date, which would otherwise
// silently produce a schedule that generates zero occurrences. Only enforced
// when both dates are present (the update payload may omit `startDate`).
export function refineScheduleDateWindow(
    value: { startDate?: string; endDate?: string | null },
    ctx: z.RefinementCtx,
): void {
    if (
        value.startDate != null &&
        value.endDate != null &&
        value.endDate < value.startDate
    ) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["endDate"],
            message: "End date must be on or after start date",
        });
    }
}

// ─── Shared field shapes ───────────────────────────────────────────────────────

export const serviceStartPositioningShape = {
    // 0-6 (Sun-Sat) for monthly weekday-occurrence positioning via
    // `monthRecurrenceType`. Weekly schedules use `weekDays` instead.
    weekDay: z.number().int().min(0).max(6).nullable().optional(),
    weekDays: z.array(z.number().int().min(0).max(6)).nullable().optional(),
    month: z.number().int().min(1).max(12).nullable().optional(),
    dateOfMonth: z.number().int().min(1).max(31).nullable().optional(),
    datesOfMonth: z
        .array(z.number().int().min(1).max(31))
        .nullable()
        .optional(),
    monthRecurrenceType: z
        .nativeEnum(MonthRecurrenceType)
        .nullable()
        .optional(),
    quarterMonth: z.nativeEnum(QuarterMonth).nullable().optional(),
};

export const workStartShape = {
    workStartDateMode: z.nativeEnum(WorkStartDateMode),
    workStartOffsetDays: z.number().int().nonnegative().nullable().optional(),
    // Only meaningful for OFFSET_DAYS_AFTER_SERVICE_END — the service layer
    // treats an omitted value as `false` (see resolveWorkStartSkipWeekend),
    // matching every other work-start sibling field's optional pattern here.
    workStartSkipWeekend: z.boolean().optional(),
    workStartWeekday: z.number().int().min(1).max(5).nullable().optional(),
    workStartRelativePeriod: z
        .nativeEnum(RelativeCalendarPeriod)
        .nullable()
        .optional(),
    workStartRelativePosition: z
        .nativeEnum(RelativeCalendarPosition)
        .nullable()
        .optional(),
    workStartRelativeDayType: z
        .nativeEnum(RelativeDayType)
        .nullable()
        .optional(),
    workStartRelativeDayOfPeriod: z
        .number()
        .int()
        .positive()
        .nullable()
        .optional(),
};

export const workEndShape = {
    workEndDateMode: z.nativeEnum(WorkEndDateMode),
    workEndOffsetDays: z.number().int().nonnegative().nullable().optional(),
    workEndRelativePeriod: z
        .nativeEnum(RelativeCalendarPeriod)
        .nullable()
        .optional(),
    workEndRelativePosition: z
        .nativeEnum(RelativeCalendarPosition)
        .nullable()
        .optional(),
    workEndRelativeDayType: z.nativeEnum(RelativeDayType).nullable().optional(),
    workEndRelativeDayOfPeriod: z
        .number()
        .int()
        .positive()
        .nullable()
        .optional(),
};

// ─── Create schema ─────────────────────────────────────────────────────────────

export const ServiceTemplateScheduleCreateSchema = z
    .object({
        templateId: z.string().uuid(),
        entityUuid: z.string().uuid(),
        workspaceId: z.string().uuid().nullable().optional(),
        // Set by the caller when creating a batch of schedules for multiple
        // entities at the same time, so the resulting schedules can be
        // identified as a single logical group. Omitted for single-entity
        // creates.
        groupId: z.string().uuid().optional(),
        period: z.nativeEnum(ServiceTemplateRecurrencePeriod),
        interval: z.number().int().positive(),
        startDate: z.string().date(),
        endDate: z.string().date().nullable().optional(),
        maxOccurrenceCount: z.number().int().positive().nullable().optional(),
        status: z.nativeEnum(ServiceTemplateRecurrenceStatus).optional(),
        applicationLeadValue: z.number().int().nonnegative().optional(),
        applicationLeadUnit: z.nativeEnum(RecurrenceLeadUnit).optional(),
        preparerId: z.number().int().nullable().optional(),
        reviewerId: z.number().int().nullable().optional(),
        managerId: z.number().int().nullable().optional(),
        clientId: z.number().int().nullable().optional(),
        ...serviceStartPositioningShape,
        ...workStartShape,
        ...workEndShape,
    })
    .superRefine(refineSchedulePositioning)
    .superRefine(refineScheduleDateWindow);

export type ServiceTemplateScheduleCreate = z.infer<
    typeof ServiceTemplateScheduleCreateSchema
>;

// ─── Update schema ─────────────────────────────────────────────────────────────

// The update payload is the create shape made fully partial: every field is
// optional and the work-start/work-end mode discriminators become optional too.
const workStartUpdateShape = {
    ...workStartShape,
    workStartDateMode: z.nativeEnum(WorkStartDateMode).optional(),
};

const workEndUpdateShape = {
    ...workEndShape,
    workEndDateMode: z.nativeEnum(WorkEndDateMode).optional(),
};

export const ServiceTemplateScheduleUpdateSchema = z
    .object({
        workspaceId: z.string().uuid().nullable().optional(),
        period: z.nativeEnum(ServiceTemplateRecurrencePeriod).optional(),
        interval: z.number().int().positive().optional(),
        startDate: z.string().date().optional(),
        endDate: z.string().date().nullable().optional(),
        maxOccurrenceCount: z.number().int().positive().nullable().optional(),
        status: z.nativeEnum(ServiceTemplateRecurrenceStatus).optional(),
        applicationLeadValue: z.number().int().nonnegative().optional(),
        applicationLeadUnit: z.nativeEnum(RecurrenceLeadUnit).optional(),
        preparerId: z.number().int().nullable().optional(),
        reviewerId: z.number().int().nullable().optional(),
        managerId: z.number().int().nullable().optional(),
        clientId: z.number().int().nullable().optional(),
        ...serviceStartPositioningShape,
        ...workStartUpdateShape,
        ...workEndUpdateShape,
    })
    .superRefine(refineSchedulePositioning)
    .superRefine(refineScheduleDateWindow);

export type ServiceTemplateScheduleUpdate = z.infer<
    typeof ServiceTemplateScheduleUpdateSchema
>;
